import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink, Observable } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { encryptSensitiveFields, decryptResponseFields, apiEncrypt, apiDecrypt } from "../utils/apiCrypto.js";

const AUTH_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";
const API_KEY_STORAGE = "widget_api_key";

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_API_URL,
});

// ─── Token storage (in-memory cache + AES-GCM encrypted localStorage) ────────
// getAuthToken() is always sync — reads from memory.
// setAuthToken() updates memory immediately, then persists encrypted async.
// On module load, _initReady decrypts any stored token into memory.
// Existing plain-text tokens are handled transparently by apiDecrypt.

let _authToken = null;
let _refreshToken = null;

export const waitForInit = (async () => {
  const rawToken = localStorage.getItem(AUTH_TOKEN_KEY);
  if (rawToken) {
    _authToken = await apiDecrypt(rawToken);
    // Migrate plaintext → encrypted on first load
    if (_authToken && !rawToken.startsWith('apie:')) {
      apiEncrypt(_authToken).then((enc) => localStorage.setItem(AUTH_TOKEN_KEY, enc));
    }
  }
  const rawRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (rawRefresh) {
    _refreshToken = await apiDecrypt(rawRefresh);
    if (_refreshToken && !rawRefresh.startsWith('apie:')) {
      apiEncrypt(_refreshToken).then((enc) => localStorage.setItem(REFRESH_TOKEN_KEY, enc));
    }
  }

  // Proactively refresh if the stored access token is already expired (or within 60s of expiry).
  // Handles the "next day" case: stored token is stale but refresh token is still valid.
  // authLink awaits waitForInit on every request, so all API calls wait for this to complete.
  if (_authToken && _refreshToken) {
    const exp = decodeJwtExp(_authToken);
    if (exp !== null && Date.now() >= exp * 1000 - 60_000) {
      const newToken = await attemptTokenRefresh();
      if (!newToken) clearAuthToken();
    }
  }
})();

// Manual atob() on a JWT's base64url payload segment was silently throwing on
// browsers that don't tolerate missing padding (base64url per the JWT spec
// omits trailing '=' entirely) - previously caught by a bare `catch {}`, so a
// decode failure had zero trace anywhere. This pads correctly and logs
// instead of swallowing failures.
function decodeJwtExp(token) {
  try {
    const seg = token.split('.')[1];
    if (!seg) return null;
    let b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch (e) {
    console.error('[auth] Failed to decode JWT expiry - proactive refresh cannot run for this token', e);
    return null;
  }
}

// The widget previously only ever checked the token's staleness once, at
// module load (waitForInit above) - a session left open on the same screen
// for longer than the access token's lifetime (no page reload to re-trigger
// waitForInit) had nothing proactively renewing it, and would only recover
// once some API call happened to fail and errorLink's reactive refresh
// kicked in. If nothing was actively polling in that window, the user would
// see the session as having silently died with no visible trigger. Checks
// every 30s and refreshes if the token is within 60s of expiry, matching the
// merchant/admin/partner portals' existing background refresh. Shares the
// same isRefreshing mutex as errorLink's reactive path below so the two
// never race each other.
setInterval(async () => {
  if (!_authToken || !_refreshToken) return;
  const exp = decodeJwtExp(_authToken);
  if (exp === null || Date.now() < exp * 1000 - 60_000) return;
  if (isRefreshing) return;
  isRefreshing = true;
  try {
    const newToken = await attemptTokenRefresh();
    isRefreshing = false;
    if (newToken) {
      drainQueue(newToken);
    } else {
      console.error('[auth] Proactive refresh failed - access token will expire without renewal');
      drainQueue(null);
    }
  } catch (e) {
    isRefreshing = false;
    console.error('[auth] Proactive refresh threw unexpectedly', e);
    drainQueue(null);
  }
}, 30_000);

export const setAuthToken = (token) => {
  if (!token) return;
  _authToken = token;
  apiEncrypt(token).then((enc) => localStorage.setItem(AUTH_TOKEN_KEY, enc));
};

export const getAuthToken = () => _authToken;

export const clearAuthToken = () => {
  _authToken = null;
  _refreshToken = null;
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const setRefreshToken = (token) => {
  if (!token) return;
  _refreshToken = token;
  apiEncrypt(token).then((enc) => localStorage.setItem(REFRESH_TOKEN_KEY, enc));
};

export const getRefreshToken = () => _refreshToken;

// ─── Refresh token logic ─────────────────────────────────────────────────────

const REFRESH_MUTATION = `
  mutation RefreshAccessToken($refreshToken: String!) {
    refreshAccessToken(refreshToken: $refreshToken) {
      success
      accessToken
      refreshToken
    }
  }
`;

let isRefreshing = false;
let pendingQueue = [];

const drainQueue = (newToken) => {
  pendingQueue.forEach((cb) => cb(newToken));
  pendingQueue = [];
};

export async function attemptTokenRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const params = new URLSearchParams(window.location.search);
  const apiKey = params.get("apikey") || import.meta.env.VITE_DEFAULT_API_KEY || localStorage.getItem(API_KEY_STORAGE);

  try {
    const res = await fetch(import.meta.env.VITE_GRAPHQL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client": "partner-widget",
        ...(apiKey && { "x-api-key": apiKey }),
      },
      body: JSON.stringify({ query: REFRESH_MUTATION, variables: { refreshToken } }),
    });
    const json = await res.json();
    const data = json?.data?.refreshAccessToken;
    if (data?.success && data.accessToken) {
      setAuthToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      return data.accessToken;
    }
    console.error('[auth] Refresh rejected by server', { message: data?.message, graphQLErrors: json?.errors });
  } catch (e) {
    console.error('[auth] Refresh request failed (network/parse error)', e);
  }
  return null;
}

const LOGOUT_BY_REFRESH_TOKEN_MUTATION = `
  mutation LogoutByRefreshToken($refreshToken: String!) {
    logoutByRefreshToken(refreshToken: $refreshToken) {
      success
    }
  }
`;

// Fallback for the guarded `logout` mutation: if the access token has already
// expired, that mutation never authenticates and the session row is never
// deactivated server-side - it then keeps showing as "active" in the admin
// panel for up to its full 7-day refresh window even though the user is back
// on the login screen. The refresh token (still valid up to 7 days) is proof
// enough of which session to close, so this works whether or not the access
// token is still good. Best-effort - a network failure here shouldn't block
// local logout, which the caller performs regardless.
export const logoutCurrentSession = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return;
  try {
    await fetch(import.meta.env.VITE_GRAPHQL_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: LOGOUT_BY_REFRESH_TOKEN_MUTATION, variables: { refreshToken } }),
    });
  } catch {
    // network error — nothing more we can do client-side
  }
};

// ─── Auth + session-expired helpers ─────────────────────────────────────────

const AUTH_ERROR_MESSAGES = [
  "session has been revoked",
  "please log in again",
  "user not found",
  "unauthorized",
  "unauthenticated",
  "invalid token",
  "jwt",
];

function isAuthError(message) {
  if (!message) return false;
  const lower = message.toLowerCase();
  return AUTH_ERROR_MESSAGES.some((m) => lower.includes(m));
}

function dispatchSessionExpired() {
  clearAuthToken();
  window.dispatchEvent(new Event("auth:session-expired"));
}

// ─── Error link: intercept UNAUTHENTICATED, try refresh, retry ──────────────

const errorLink = onError(({ graphQLErrors, networkError, operation, forward, response }) => {
  const allErrors = graphQLErrors || response?.errors || [];
  const isUnauth =
    allErrors.some((e) => isAuthError(e.message) || e.extensions?.code === "UNAUTHENTICATED") ||
    isAuthError(networkError?.message) ||
    isAuthError(networkError?.result?.errors?.[0]?.message) ||
    networkError?.statusCode === 401;

  if (!isUnauth) return;

  return new Observable((observer) => {
    if (isRefreshing) {
      pendingQueue.push((newToken) => {
        if (!newToken) {
          observer.error(new Error("Session expired"));
          return;
        }
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, Authorization: `Bearer ${newToken}` },
        }));
        forward(operation).subscribe(observer);
      });
      return;
    }

    isRefreshing = true;
    attemptTokenRefresh()
      .then((newToken) => {
        isRefreshing = false;
        if (newToken) {
          drainQueue(newToken);
          operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, Authorization: `Bearer ${newToken}` },
          }));
          forward(operation).subscribe(observer);
        } else {
          drainQueue(null);
          dispatchSessionExpired();
          observer.error(new Error("Session expired"));
        }
      })
      .catch(() => {
        isRefreshing = false;
        drainQueue(null);
        dispatchSessionExpired();
        observer.error(new Error("Session expired"));
      });
  });
});

// ─── Auth link ────────────────────────────────────────────────────────────────

const authLink = setContext(async (_, { headers }) => {
  await waitForInit;
  const params = new URLSearchParams(window.location.search);
  const apiKey = params.get("apikey")
    || import.meta.env.VITE_DEFAULT_API_KEY
    || localStorage.getItem(API_KEY_STORAGE);
  const token = getAuthToken();

  if (apiKey) localStorage.setItem(API_KEY_STORAGE, apiKey);

  return {
    headers: {
      ...headers,
      "x-client": "partner-widget",
      ...(apiKey && { "x-api-key": apiKey }),
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
});

// ─── Crypto link ──────────────────────────────────────────────────────────────

const cryptoLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    let pendingCount = 0;
    let isComplete = false;

    const tryComplete = () => {
      if (isComplete && pendingCount === 0) observer.complete();
    };

    const run = async () => {
      try {
        operation.variables = await encryptSensitiveFields(operation.variables || {});
      } catch {}

      forward(operation).subscribe({
        next: (response) => {
          const responseErrors = response?.errors;
          if (responseErrors?.length) {
            const hasAuthErr = responseErrors.some(
              (err) => isAuthError(err.message) || err.extensions?.code === "UNAUTHENTICATED"
            );
            if (hasAuthErr) {
              // Apollo 4's onError only fires for Observable errors, not next events.
              // Emit as an Observable error so errorLink's networkError handler
              // intercepts it, refreshes the token, and retries.
              const authErr = Object.assign(new Error("UNAUTHENTICATED"), { statusCode: 401 });
              observer.error(authErr);
              return;
            }
          }

          pendingCount++;
          (async () => {
            try {
              if (response.data) {
                response = { ...response, data: await decryptResponseFields(response.data) };
              }
            } catch {}
            observer.next(response);
            pendingCount--;
            tryComplete();
          })();
        },
        error: (err) => {
          // Always propagate as an observable error so errorLink can
          // attempt a token refresh before deciding to expire the session.
          observer.error(err);
        },
        complete: () => {
          isComplete = true;
          tryComplete();
        },
      });
    };

    run().catch((err) => observer.error(err));
  });
});

const client = new ApolloClient({
  link: errorLink.concat(cryptoLink).concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: "network-only" },
    query: { fetchPolicy: "network-only" },
    mutate: { fetchPolicy: "no-cache" },
  },
});

export default client;
